<template>
	<!-- logos -->
	<div class="bg-color-content-primary">
		<BasePlaceHolder
			:placehold="logosLoading"
			:line-count="1"
			theme="dark"
			:class="{ 'container p-200': logosLoading }"
		>
			<LogosList
				v-if="logos.length"
				:heading="logosHeading"
				:logos="logos"
				:opaque="true"
				marquee-duration="45s"
				marquee-gap="3rem"
				marquee-logos-size="1.75rem"
			></LogosList>
		</BasePlaceHolder>
	</div>
	<BasePlaceHolder :placehold="loading" :section-count="3" :class="{ 'container py-200': loading }">
		<VentureCapitalAboutTheFund
			theme="light"
			:show-sectors="false"
			:stats="ventureImportantStats"
			:disclaimer="ventureFundDisclaimer"
		/>
		<GrowthTechCompanies v-if="assetElements.length" :asset-elements="assetElements" />
		<div v-if="literatureItems.length" class="container py-200 py-400-md">
			<h2 class="heading-4 mb-50 mb-150">Literature</h2>
			<LiteratureSection
				:literature-list="literatureItems"
				:disclaimer="literatureDisclaimer"
				disclaimer-classes="body-lg-md"
			/>
		</div>
	</BasePlaceHolder>
</template>

<script setup lang="ts">
import { Ref, ref } from 'vue';
import { SanityImage, SanityRichTextBlock } from 'types/sanity';
import { Asset } from 'types/assets';
import { CustomSpacetimeFormat } from 'types/spacetime-formats';
import { FeaturedFundSectionData } from 'types/marketing-site';
import { FundLiterature } from 'types/funds';
import { getFund } from '@sanity/proxy/funds';
import { getMarketingPageVentureAssets } from '@sanity/proxy/assets';
import { getVentureMarketingPage } from '@sanity/graphql/venture-marketing-page';
import GrowthTechCompanies from '@components/offering-details/growth-tech-companies.vue';
import { growthTechReitId } from '@constants/reit';
import LiteratureSection from '@components/offering-details/literature-section.vue';
import LogosList from '@components/marketing-site/logos-list.vue';
import { percentage } from '@filters/shared-filters';
import { PERFORMANCE_TIMEZONE } from '@constants/timezones';
import spacetime from 'spacetime';
import { useIdleCallback } from '@utils/composables/use-idle-callback';
import VentureCapitalAboutTheFund from '@components/marketing-site/venture-capital/venture-capital-about-the-fund.vue';
import { VentureImportantStat } from 'types/sanity/venture-marketing';

const logosLoading: Ref<boolean> = ref(true);
const loading: Ref<boolean> = ref(true);

const logosHeading: Ref<string> = ref('Featured Companies');
const logos: Ref<Array<string>> = ref([]);
const featuredFundSectionData = ref<FeaturedFundSectionData>({
	title: 'Innovation Fund',
	description: '',
	fundStats: [],
	ctaLink: {
		text: 'Learn more about the Innovation Fund',
		router: 'view-offering-details',
		params: { offeringId: growthTechReitId }
	},
	assetTypes: [
		{ icon: 'flagship-buildforrent', label: 'Build for rent' },
		{ icon: 'flagship-multifamilyapartments', label: 'Multifamily apartments' },
		{ icon: 'flagship-industrialproperties', label: 'Industrial properties' }
	]
});
const ventureImportantStats: Ref<Array<VentureImportantStat> | null> = ref(null);
const ventureFundDisclaimer: Ref<string | null> = ref(null);
const assetElements: Ref<Array<Asset>> = ref([]);
const literatureItems: Ref<Array<FundLiterature>> = ref([]);
const literatureDisclaimer: Ref<Array<SanityRichTextBlock>> = ref([]);

useIdleCallback(async () => {
	try {
		const [assetsData] = await Promise.all([getMarketingPageVentureAssets()]);

		assetElements.value = assetsData;
	} catch {
	} finally {
		loading.value = false;
	}
});

async function setup() {
	try {
		const [marketingPageData, fundData] = await Promise.all([
			getVentureMarketingPage('campaign-venture'),
			getFund(growthTechReitId, { include: ['HISTORICAL_RETURNS'] })
		]);
		if (marketingPageData?.ventureLogos) {
			logos.value = marketingPageData.ventureLogos.logos.map((logo: SanityImage) => logo.asset.url);
			logosHeading.value = marketingPageData.ventureLogos.logosHeading ?? logosHeading.value;
		}
		if (fundData) {
			featuredFundSectionData.value.description = fundData.fundSummary ?? '';
			literatureItems.value = fundData.literature ?? [];
			literatureDisclaimer.value = fundData.literatureDisclaimer ?? [];

			// build custom stats
			const lastYear = spacetime.now(PERFORMANCE_TIMEZONE).subtract(1, 'year').year().toString();
			const lastYearReturn = fundData.historicalPerformanceReturns!.find((r) => r.year === lastYear);
			if (lastYearReturn && fundData.fundAum) {
				const currentDate = spacetime().format(CustomSpacetimeFormat.MONTH_DATE_YEAR);
				ventureImportantStats.value = [
					{
						label: `${lastYear} Total return`,
						value: percentage(lastYearReturn.months.pop()!.totalYearToDateReturn, true)
					},
					{
						label: 'Net asset value (NAV)',
						value: fundData.fundAum,
						tooltip: `The net value of the fund's assets less its liabilities as of ${currentDate}.`
					}
				];
				ventureFundDisclaimer.value = `Past performance is no guarantee of future results. Any historical returns, expected returns, or probability projections may not reflect actual future performance. All securities involve risk and may result in partial or total loss. Returns are net of fees.`;
			}
		}
	} finally {
		logosLoading.value = false;
	}
}

setup();
</script>
